import { z } from 'zod';
import { INTAKE_TIERS } from './intake.js';
import { SIGNAL_KINDS } from './outside-in.js';
import { SCORE_BANDS } from './scoring.js';
/**
 * Alerts and automation templates (task 70, decision 0046). An alert rule watches the book for one kind
 * of change (a score drop, a failed kill switch, an overdue condition, an expiring document, a new
 * outside-in finding) and tells the people in its audience: always in the app, and by email at once or
 * in a daily digest. Automation templates chase what is late (renewal checks, conditions) and route new
 * submissions, each with a dry run that says what it would do without doing it.
 */
/** Who can be told: an alert rule's audience is some of these. */
export const ALERT_AUDIENCE = ['tenant_admin', 'underwriter', 'broker', 'auditor'];
export const AUDIENCE_LABEL = {
    tenant_admin: 'Admins',
    underwriter: 'Underwriters',
    broker: 'Brokers (their clients)',
    auditor: 'Auditors',
};
export const ALERT_KINDS = [
    'score_drop',
    'kill_switch',
    'condition_overdue',
    'document_expiring',
    'outside_in_finding',
    'decision_waiting',
];
export const ALERT_KIND_LABEL = {
    score_drop: 'Score drops',
    kill_switch: 'A kill switch fails',
    condition_overdue: 'A condition is overdue',
    document_expiring: 'A document is expiring',
    outside_in_finding: 'New outside-in finding',
    decision_waiting: 'A decision waits for sign-off',
};
export const ALERT_KIND_HELP = {
    score_drop: 'When a client’s CyberScore falls by at least the points you set, or falls below a band.',
    kill_switch: 'When a control that caps the score newly fails (for example no MFA on email).',
    condition_overdue: 'When a condition of an underwriting decision is still open after its due date.',
    document_expiring: 'When a document the business gave (a certificate, a policy) expires within the days you set.',
    outside_in_finding: 'When an outside-in check finds something the last one didn’t (an open port, leaked credentials…).',
    decision_waiting: 'When a proposed cover decision has waited at least the days you set for a second underwriter (task 80).',
};
/** Checked daily rather than as things happen. */
export const DAILY_KINDS = [
    'condition_overdue',
    'document_expiring',
    'decision_waiting',
];
export const ALERT_EMAIL = ['off', 'instant', 'digest'];
export const ALERT_EMAIL_LABEL = {
    off: 'In the app only',
    instant: 'Email at once',
    digest: 'Daily email digest',
};
const BAND_KEYS = SCORE_BANDS.map((b) => b.key);
export const AlertParams = z.strictObject({
    /** score_drop: a fall of at least this many points. */
    points: z.number().int().min(5).max(500).optional(),
    /** score_drop: falling below this band. */
    band_below: z.enum(BAND_KEYS).optional(),
    /**
     * condition_overdue: overdue by at least this many days; document_expiring: expiring within them;
     * decision_waiting: waiting at least this many days.
     */
    days: z.number().int().min(0).max(365).optional(),
    /** outside_in_finding: only these kinds of finding (all when left out). */
    signal_kinds: z.array(z.enum(SIGNAL_KINDS)).min(1).optional(),
});
const ruleBase = {
    name: z.string().trim().min(1).max(80),
    kind: z.enum(ALERT_KINDS),
    params: AlertParams.default({}),
    audience: z.array(z.enum(ALERT_AUDIENCE)).min(1).max(4),
    email: z.enum(ALERT_EMAIL).default('off'),
    active: z.boolean().default(true),
};
/** Params that make sense for the kind; the rest are refused rather than ignored. */
function checkParams(r, ctx) {
    const allowed = {
        score_drop: ['points', 'band_below'],
        kill_switch: [],
        condition_overdue: ['days'],
        document_expiring: ['days'],
        outside_in_finding: ['signal_kinds'],
        decision_waiting: ['days'],
    };
    for (const k of Object.keys(r.params))
        if (r.params[k] !== undefined && !allowed[r.kind].includes(k))
            ctx.addIssue({
                code: 'custom',
                path: ['params', k],
                message: `Not used by “${ALERT_KIND_LABEL[r.kind]}”`,
            });
    if (r.kind === 'score_drop' && r.params.points === undefined && r.params.band_below === undefined)
        ctx.addIssue({ code: 'custom', path: ['params', 'points'], message: 'Set the points, a band, or both' });
}
export const AlertRuleInput = z.strictObject(ruleBase).superRefine(checkParams);
export const AlertRulePatch = z
    .strictObject({ revision: z.number().int().min(1), ...ruleBase })
    .superRefine(checkParams);
export const MarkAlertsRead = z.strictObject({
    /** These alerts; all of the person's when left out. */
    ids: z.array(z.uuid()).min(1).max(200).optional(),
});
// —— what fires ——
const bandRank = (b) => BAND_KEYS.indexOf(b);
/** A score drop worth telling: by the points, or across the band line (once, as it crosses). */
export function scoreDrop(params, previous, now) {
    if (!previous)
        return null;
    const fell = previous.score - now.score;
    const reasons = [];
    if (params.points !== undefined && fell >= params.points)
        reasons.push(`down ${fell} points`);
    if (params.band_below !== undefined) {
        const line = bandRank(params.band_below);
        if (bandRank(now.band) < line && bandRank(previous.band) >= line)
            reasons.push(`below ${bandLabel(params.band_below)}`); // prettier-ignore
    }
    return reasons.length ? `CyberScore ${previous.score} → ${now.score} (${reasons.join(', ')})` : null;
}
export const bandLabel = (b) => b
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (c) => c.toUpperCase());
/** Kill switches failing now that weren't failing before. */
export const newlyFailed = (before, now) => {
    const was = new Set(before);
    return [...new Set(now)].filter((c) => !was.has(c)).sort();
};
/** What tells one reading from another of its kind on the same asset (a port, a product version…). */
const IDENTITY = {
    open_port: ['port'],
    software: ['product', 'version'],
    tls: ['protocol', 'trusted'],
    email_auth: ['spf', 'dkim', 'dmarc'],
    http_headers: ['hsts', 'csp', 'frame_options'],
    leaked_credentials: ['latest_breach'],
};
/** A reading's identity across runs: the same kind, asset and identifying fields. */
export const signalKey = (s) => `${s.kind}:${stable(s.asset)}:${(IDENTITY[s.kind] ?? []).map((f) => stable(s.data?.[f] ?? null)).join('|')}`;
const stable = (v) => v && typeof v === 'object'
    ? Array.isArray(v)
        ? `[${v.map(stable).join(',')}]`
        : `{${Object.keys(v).sort().map((k) => `${k}:${stable(v[k])}`).join(',')}}` // prettier-ignore
    : JSON.stringify(v);
/** Findings in this run that the previous run didn't have (every finding when there was no previous run). */
export function newFindings(previous, now, kinds) {
    // prettier-ignore
    const had = new Set((previous ?? []).map(signalKey));
    const seen = new Set();
    return now.filter((s) => {
        const k = signalKey(s);
        if (had.has(k) || seen.has(k) || (kinds && !kinds.includes(s.kind)))
            return false;
        seen.add(k);
        return true;
    });
}
/** A short description of a reading: where, and what (port 3389 rdp, Exchange Server 2013…). */
export function readingText(s) {
    const a = s.asset;
    const where = a && typeof a === 'object' && 'value' in a ? String(a.value) : stable(s.asset);
    const d = s.data ?? {};
    const what = s.kind === 'open_port'
        ? `port ${String(d.port)}${d.service ? ` (${String(d.service)})` : ''}`
        : s.kind === 'software'
            ? [d.product, d.version].filter(Boolean).join(' ')
            : s.kind === 'leaked_credentials'
                ? `${String(d.accounts ?? '?')} accounts, latest breach ${String(d.latest_breach ?? '?')}`
                : (IDENTITY[s.kind] ?? []).map((f) => `${f.replace(/_/g, ' ')} ${String(d[f])}`).join(', ');
    return `${where}: ${what}`;
}
// —— automation templates ——
export const AUTOMATION_TEMPLATES = ['renewal_chase', 'condition_chase', 'submission_routing'];
export const AUTOMATION_LABEL = {
    renewal_chase: 'Chase renewal checks',
    condition_chase: 'Chase overdue conditions',
    submission_routing: 'Route new submissions',
};
export const AUTOMATION_HELP = {
    renewal_chase: 'Emails the business when a renewal check from a campaign is still being answered after the days you set, and again every few days.', // prettier-ignore
    condition_chase: 'Emails the business when a condition is overdue, and again every few days until it is met or waived.', // prettier-ignore
    submission_routing: 'Sends each new submission (of the tiers you pick) to one underwriter by email.',
};
export const AutomationParams = z.discriminatedUnion('template', [
    z.strictObject({
        template: z.literal('renewal_chase'),
        after_days: z.number().int().min(1).max(60),
        every_days: z.number().int().min(2).max(30),
    }),
    z.strictObject({
        template: z.literal('condition_chase'),
        overdue_days: z.number().int().min(0).max(60),
        every_days: z.number().int().min(2).max(30),
    }),
    z.strictObject({
        template: z.literal('submission_routing'),
        to_user: z.uuid(),
        tiers: z.array(z.enum(INTAKE_TIERS)).min(1).max(3),
    }),
]);
export const AUTOMATION_DEFAULTS = {
    renewal_chase: { after_days: 7, every_days: 7 },
    condition_chase: { overdue_days: 0, every_days: 7 },
    submission_routing: { tiers: ['quick', 'standard', 'full'] },
};
export const SaveAutomation = z.strictObject({
    params: AutomationParams,
    active: z.boolean(),
    /** 0 when it hasn't been saved yet. */
    revision: z.number().int().min(0),
});
//# sourceMappingURL=alerts.js.map